import React, { useRef, useState, useEffect } from 'react';
import { ReactComponent as TargetUndo } from '../../assets/svg/undoTarget.svg';
import { ReactComponent as Save } from '../../assets/svg/Save.svg';

import '../../styles/modals/StudentInfoModal.css';
import { Controller, useForm } from 'react-hook-form';
import Loading from '../Loading';
import { useNavigate, useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { createUpdatePractice, getLocales, getPracticeById, uploadFile } from '../../server/server';
import { fileSystemUrl } from '../../server/connections';
import CategoriesDropDown from '../courses/CategoriesDropDown';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import PracticeSingleStep from './PracticeSingleStep';
import { notify } from '../../utils/util';
import ImageComponent from '../ImageComponent';

const INITIALSTEP = {
	name: '',
	description: '',
	order: 1,
};

function SinglePracticeScreen() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [hoveredButton, setHoveredButton] = useState(false);
	const [practice, setPractice] = useState<any>(null);
	const [loading, setLoading] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [image, setImage] = useState<string | undefined>(undefined);
	const [imageId, setImageId] = useState('');
	const [locales, setLocales] = useState([]);
	const [locale, setLocale] = useState('Select');
	const [steps, setSteps] = useState([INITIALSTEP]);
	const [practiceNameError, setPracticeNameError] = useState(false);
	const [practiceDescriptionError, setPracticeDescriptionError] = useState(false);
	const [imageIdError, setImageIdError] = useState(false);

	const { register, handleSubmit, watch, reset, formState, getValues, setValue, control } = useForm({
		defaultValues: {
			name: '',
			shortDescription: '',
			description: '',
			duration: 0,
			localeId: '',
		},
	});
	const { isDirty, isValid, errors } = formState;

	useEffect(() => {
		loadLocales();
	}, []);

	useEffect(() => {
		if (id && id !== 'new') loadPractice(id);
	}, [id]);

	useEffect(() => {
		if (!!practice?.id) {
			setValue('name', practice?.name);
			setValue('shortDescription', practice?.shortDescription);
			setValue('description', practice?.description);
			setValue('duration', practice?.duration);
			setValue('localeId', practice.locale?.id);
			setLocale(practice.locale?.name ?? 'Select');
			let list = practice.steps.map((item: any) => {
				delete item.id;
				return item;
			});
			setSteps(list);
		}
		if (!!practice?.image) {
			setImage(fileSystemUrl + '/' + practice.image?.url);
			setImageId(practice?.image?.id);
		}
	}, [practice]);

	const loadPractice = async (id: string) => {
		setLoading(true);
		const resp = await getPracticeById(id);
		if (resp.status == 200) {
			setPractice(resp.data);
			setSteps(resp.data?.steps);
			setLoading(false);
		} else {
			setLoading(false);
		}
	};

	const loadLocales = async () => {
		const resp = await getLocales();
		if (resp.status === 200) {
			setLocales(resp.data);
		}
	};

	const validate = () => {
		let valid = true;
		steps.forEach((element: any) => {
			if (element.name === '') {
				setPracticeNameError(true);
				valid = false;
				return;
			}
		});
		steps.forEach((element: any) => {
			if (element.description === '') {
				setPracticeDescriptionError(true);
				valid = false;
				return;
			}
		});
		return valid;
	};

	const onSubmit = async () => {
		if (!validate()) return;
		if (practice === null) {
			createPractice();
		} else {
			updatePractice();
		}
	};

	const createPractice = async () => {
		setProcessing(true);
		let newPractice: any = getValues();
		newPractice.duration = isNaN(parseInt(newPractice.duration)) ? 0 : parseInt(newPractice.duration);
		newPractice.imageId = imageId ?? null;
		let list = steps.map((item: any, index: number) => {
			item.order = index + 1;
			return item;
		});
		newPractice.steps = list;
		const resp = await createUpdatePractice(newPractice);
		if (resp.status === 201) {
			const practiceFresh = resp.data;
			notify('Practice is created!');
			//navigate(`/practices/${practiceFresh.id}`);

			setProcessing(false);
		} else {
			setProcessing(false);
		}
	};

	const updatePractice = async (file: any = null) => {
		setProcessing(true);
		let newPractice: any = getValues();
		newPractice.id = practice?.id;
		newPractice.duration = isNaN(parseInt(newPractice.duration)) ? 0 : parseInt(newPractice.duration);
		newPractice.imageId = imageId ?? null;
		let list = steps.map((item: any, index: number) => {
			item.order = index + 1;
			return item;
		});
		newPractice.steps = list;
		const resp = await createUpdatePractice(newPractice);
		if (resp.status === 201) {
			const courseFresh = resp.data;
			notify('Practice is updated!');
			setProcessing(false);
		} else {
			setProcessing(false);
		}
	};

	const setSelectedLocaleId = (item: any) => {
		setValue('localeId', item?.id, { shouldValidate: true });
		setLocale(!!item?.name ? item.name : 'Select');
	};

	const addNewStep = () => {
		setSteps((prev: any) => [
			...prev,
			{
				name: '',
				description: '',
				order: steps.length + 1,
			},
		]);
	};

	const handleDropQuestion = (droppedItem: any) => {
		// Ignore drop outside droppable container
		if (!droppedItem.destination) return;
		var updatedList = [...steps];
		// Remove dragged item
		const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
		// Add dropped item
		updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
		// Update State
		let newReorderList = updatedList.map((item, index) => {
			return { ...item, order: index + 1 };
		});
		setSteps(newReorderList);
	};

	return (
		<div className='flex h-full flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className=' w-full max-w-[1200px] px-[10px] '>
				<form onSubmit={handleSubmit(onSubmit)} className='w-full  bg-white flex flex-col px-[40px] py-[30px] rounded-[10px]'>
					{loading && (
						<div className='w-full flex flex-row items-center justify-center min-h-[600px]'>
							<Loading size={70} primaryColor='#5E639B' stroke={3} />
						</div>
					)}

					{!loading && (
						<div className='flex flex-col gap-5'>
							<div className='flex flex-row justify-between mt-5'>
								<div className='flex flex-row  gap-10'>
									<div
										className=' flex cursor-pointer mr-5 items-center justify-center bg-primaryViolet hover:bg-darkViolet  min-w-[56px] w-[56px] h-[56px] rounded-full'
										onClick={() => navigate(-1)}
										onMouseEnter={() => setHoveredButton(true)}
										onMouseLeave={() => setHoveredButton(false)}
									>
										<TargetUndo fill={hoveredButton ? '#FDDF6D' : '#FFFFFF'} />
									</div>
									<div className='flex-1'>
										<p className='text-primaryViolet font-semibold text-3xl text-left'>{practice === null ? 'Create new practice' : 'Edit practice'}</p>
									</div>
								</div>
							</div>
							<div>
								<div className='w-full flex flex-row gap-6'>
									<div className='flex-1'>
										<p className='text-primaryViolet font-semibold text-lg text-left'>Practice name</p>
										<div className={`border-b  p-2 mt-2 flex flex-row items-center ${errors.name?.type === 'required' ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
											<input
												className={`w-full text-primaryViolet ${errors.name?.type === 'required' ? 'placeholder:text-redError' : ''}`}
												placeholder={`Type practice name`}
												{...register('name', { required: true })}
												style={{ backgroundColor: 'transparent' }}
											/>
											<p className='text-redError   text-nowrap'>{errors.name?.type === 'required' ? '*This field is required' : ''}</p>
										</div>
									</div>
									<ImageComponent image={image} setImage={setImage} imageId={imageId} setImageId={setImageId} />
								</div>
								<div className='mt-4'>
									<div className='flex flex-row items-end justify-between gap-5'>
										<p className={`text-primaryViolet font-semibold text-lg text-left ${errors.shortDescription?.type === 'required' ? 'text-redError' : 'text-primaryViolet'}`}>
											Short description
										</p>

										<p className='text-redError font-medium text-lg  '>{errors.shortDescription?.type === 'required' ? '* This field is required' : ''}</p>
									</div>
									<Controller
										control={control}
										name='shortDescription'
										rules={{ required: true }}
										render={({ field }) => (
											<MDEditor
												data-color-mode='light'
												value={field.value}
												onChange={(value: any) => setValue('shortDescription', value, { shouldValidate: true })}
												preview='edit'
											/>
										)}
									/>
								</div>
								<div className='mt-4'>
									<div className='flex flex-row items-end justify-between gap-5'>
										<p className={`text-primaryViolet font-semibold text-lg text-left ${errors.description?.type === 'required' ? 'text-redError' : 'text-primaryViolet'}`}>
											Description
										</p>

										<p className='text-redError font-medium text-lg  '>{errors.description?.type === 'required' ? '* This field is required' : ''}</p>
									</div>
									<Controller
										control={control}
										name='description'
										rules={{ required: true }}
										render={({ field }) => (
											<MDEditor data-color-mode='light' value={field.value} onChange={(value: any) => setValue('description', value, { shouldValidate: true })} preview='edit' />
										)}
									/>
								</div>
								<div className='mt-5'>
									<p className='text-primaryViolet font-semibold text-lg text-left'>Practice duration</p>
									<div className={`border-b  p-2 mt-2 flex flex-row items-center ${errors.duration?.type === 'required' ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
										<input
											className={`w-full text-primaryViolet ${errors.duration?.type === 'required' ? 'placeholder:text-redError' : ''}`}
											placeholder={`Type practice duration`}
											{...register('duration', {
												required: '* Field is required!',
												pattern: {
													value: /^[1-9]\d*$/, // Dozvoljava pozitivan ceo broj
													message: 'Must be a positive numeric value!',
												},
											})}
											style={{ backgroundColor: 'transparent' }}
										/>
										<p className='text-redError   text-nowrap'>{errors.duration?.message}</p>
									</div>
								</div>
								<div className='mt-4'>
									<CategoriesDropDown
										list={locales}
										register={register}
										registerField='localeId'
										errors={errors.localeId?.type}
										label='Locales'
										selectedValue={locale}
										setSelectedValue={setSelectedLocaleId}
									/>
								</div>
							</div>
							<div>
								<div className='flex flex-col flex-1'>
									<div className='program_days' style={{ display: 'block', padding: 0 }}>
										<p className='text-left text-primaryViolet font-bold text-lg mb-1'>List of steps</p>
										<DragDropContext onDragEnd={handleDropQuestion}>
											<Droppable droppableId='droppable'>
												{(provided, snapshot) => (
													<div ref={provided.innerRef} {...provided.droppableProps}>
														{steps?.map((step: any, index: number) => (
															<Draggable key={index.toString()} draggableId={index.toString()} index={index}>
																{(provided: any, snapshot) => (
																	<PracticeSingleStep
																		provided={provided}
																		step={step}
																		index={index}
																		steps={steps}
																		setSteps={setSteps}
																		practiceNameError={practiceNameError}
																		practiceDescriptionError={practiceDescriptionError}
																	/>
																)}
															</Draggable>
														))}
														{provided.placeholder}
														<div
															className='pointer my-5 flex w-full items-center justify-center rounded-xl  bg-mudPurple py-4 text-base font-semibold text-white hover:bg-deepPurple'
															onClick={() => addNewStep()}
														>
															Add step {steps.length + 1}
														</div>
													</div>
												)}
											</Droppable>
										</DragDropContext>
									</div>
								</div>
							</div>
							<div className='flex flex-row justify-end w-full gap-5'>
								{/* <div
									className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-[#fafaff]  h-[60px] button-transparent-class'
									onClick={() => navigate(`/practices`)}
								>
									<p className='text-[18px] text-primaryViolet leading-5 font-semibold mr-3 '>Back</p>
								</div> */}
								<button
									className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer  rounded-full bg-primaryViolet  min-h-[60px] button-save-class transition'
									disabled={processing}
								>
									{processing ? (
										<Loading primaryColor='#FDDF6D' secondaryColor='#5E639B' size={25} stroke={7} />
									) : (
										<div className='flex flex-row items-center justify-center'>
											<p className='text-[18px]  leading-5 font-semibold mr-3 '>Save</p>
											<Save />
										</div>
									)}
								</button>
							</div>
						</div>
					)}
				</form>
			</div>
		</div>
	);
}

export default SinglePracticeScreen;
